import { useState } from 'react';
import Cookies from 'js-cookie';

import useLocale from '@/hooks/useLocale';
import useFeature from '@/hooks/useFeature';
import useAuth from '@/context/AuthContext';
import { FeatureFlag } from '@/core/featureFlags';
import { getLocaleInfo, getAuthTokenClient } from '@/utils/helpers';

import { fetchAndConvertCart } from '@/lib/cartAdapter';
import { ShopifyCart, ShopifyCartItem } from '@/lib/shopify';

import { buildSubscriptionPlanKey } from './payloadBuilders/buildSubscriptionPlanKey';
import { buildPlayKitSubscriptionKey } from './payloadBuilders/buildPlayKitSubscriptionKey';
import { buildGiftSubscriptionKey } from './payloadBuilders/buildGiftSubscriptionKey';

import { redirectToPlayGeniusCheckout } from './utils/redirectToPlayGeniusCheckout';
import { startPlayGeniusCheckout } from './utils/startPlayGeniusCheckout';
import { organizeCartItems } from './utils/cartHelpers';
import { useSearchParams } from 'next/navigation';

import {
  GiftSubscriptionSettings,
  PlayKitSubscriptionSettings,
  SubscriptionAddon,
  SubscriptionPlanSettings,
} from './payloadBuilders/types';

export interface StartPlayGeniusCheckoutPayload extends ShopifyCart {
  user_uuid?: string;
  gifts?: GiftSubscriptionSettings;
  bundle_products?: ShopifyCartItem[];
  subscription_plans?: SubscriptionPlanSettings[];
  subscriptions?: [PlayKitSubscriptionSettings];
}

export enum PlayGeniusCheckoutSource {
  PlayKitSubscriptionFlow = 'subflow',
  GiftSubscriptionFlow = 'gift-subflow',
  ProductPage = 'pdp',
  Cart = 'cart',
  AddMoreSaveMore = 'add-more-save-more',
}

export function usePlayGeniusCheckout(source: PlayGeniusCheckoutSource) {
  const searchParams = useSearchParams();
  const fbclid = searchParams?.get('fbclid') ?? '';

  const referralCode =
    source === PlayGeniusCheckoutSource.Cart
      ? Cookies.get('referral_code')
      : undefined;
  const [errorOccurred, setErrorOccurred] = useState<boolean>(false);

  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);

  const { user } = useAuth();
  const userUuid = user?.attributes.loveveryId;

  // Get promotion campaign key using useFeature
  // This works in both App Router and Pages Router contexts
  const promoCampaignKey = useFeature(
    FeatureFlag.MONEY_PROMOTION_CAMPAIGN,
    'off',
  );

  const flags = {
    money_promotion_campaign: promoCampaignKey,
  };

  async function continueToPlayGeniusCheckout() {
    if (typeof window === 'undefined') return;

    // reset error state
    if (errorOccurred) {
      setErrorOccurred(false);
    }

    // Use Storefront GraphQL API instead of AJAX Cart API
    const cartResult = await fetchAndConvertCart();
    if (!cartResult.ok) {
      setErrorOccurred(true);
      return;
    }

    const shopifyCartData: ShopifyCart = cartResult.data;
    const originalCartItems = shopifyCartData.items;
    const emptyCart: ShopifyCartItem[] = [];

    const payload: StartPlayGeniusCheckoutPayload = {
      ...shopifyCartData,
      items: emptyCart,
      user_uuid: userUuid,
    };

    let isRecurringPuf = false;

    const {
      coursePackProducts,
      playKitGiftSubscriptionProduct,
      playKitSubscriptionProduct,
      playKitSubscriptionBookBundleAddonProduct,
      subscriptionPlanProducts,
      standaloneProducts,
    } = organizeCartItems(originalCartItems);

    // Diverted Code Path: Play Kit Gift Subscription Checkouts short circuit.
    // This means if this product is in the cart, all other cart items are ignored,
    // and we build a custom tailored payload, only including the gift subscription product.
    if (playKitGiftSubscriptionProduct) {
      const giftSubscriptionSettings = buildGiftSubscriptionKey(
        playKitGiftSubscriptionProduct,
      );

      if (!giftSubscriptionSettings) {
        // If the Play Kit Gift Subscription Product was not added to the cart with expected cart line item properties, we cannot proceed.
        // Invoker can determine how to handle this error.
        setErrorOccurred(true);
      } else {
        // Otherwise, we can proceed with api interaction to start the checkout process.
        const giftSubscriptionCheckoutPayload = {
          ...payload,
          gifts: giftSubscriptionSettings,
          items: [
            {
              handle: 'subscription-gifting',
            },
          ] as unknown as ShopifyCartItem[],
        };

        try {
          const userJwt = await getAuthTokenClient();
          const response = await startPlayGeniusCheckout({
            payload: giftSubscriptionCheckoutPayload,
            flags,
            source,
            userJwt,
            fbclid,
          });

          await redirectToPlayGeniusCheckout({
            invoiceId: response.uuid,
            isRecurringPuf: false,
            localeInfo,
            source,
            discountCode: referralCode,
          });
        } catch (error) {
          // If the API returns an error, we can not proceed with redirecting the user to PlayGenius checkout.
          // Invoker can determine how to handle this error.
          setErrorOccurred(true);
        }
      }

      return;
    }

    // Primary Code Path: all other carts.
    payload.items = standaloneProducts;
    payload.bundle_products = coursePackProducts;

    if (playKitSubscriptionProduct) {
      const playKitSubscriptionSettings = buildPlayKitSubscriptionKey(
        playKitSubscriptionProduct,
        playKitSubscriptionBookBundleAddonProduct,
      );

      if (playKitSubscriptionSettings) {
        // ensure add_ons is an array
        const { add_ons: addOns } = playKitSubscriptionSettings;

        if (addOns) {
          const add_ons = Array.isArray(addOns)
            ? addOns
            : (Object.values(addOns) as SubscriptionAddon[]);

          payload.subscriptions = [
            {
              ...playKitSubscriptionSettings,
              add_ons,
            },
          ];
        } else {
          payload.subscriptions = [playKitSubscriptionSettings];
        }

        isRecurringPuf = false;
      }
    }

    if (subscriptionPlanProducts.length) {
      const subscriptionPlanSettings = buildSubscriptionPlanKey(
        subscriptionPlanProducts,
      );

      if (subscriptionPlanSettings) {
        payload.subscription_plans = subscriptionPlanSettings;
      }
    }

    try {
      const userJwt = await getAuthTokenClient();
      const response = await startPlayGeniusCheckout({
        payload,
        flags,
        source,
        userJwt,
        fbclid,
      });

      await redirectToPlayGeniusCheckout({
        invoiceId: response.uuid,
        localeInfo,
        source,
        isRecurringPuf,
        discountCode: referralCode,
      });
    } catch (error) {
      // use case: we ran into an error redirecting to PG checkout.
      // Invoker can determine how to handle this error.
      setErrorOccurred(true);
    }
  }

  return {
    continueToPlayGeniusCheckout,
    errorOccurred,
  };
}
